import { useContext, useEffect, useRef } from 'react';
import { DistanceContext } from './context';
import type { LocationId } from '@seaber/common/core/entries';

export const useDistances = () => {
  const context = useContext(DistanceContext);

  if (!context) {
    throw new Error('useDistances must be used within DistanceProvider');
  }

  return context;
};

export const useSingleDistance = (
  fromLocationId: LocationId | null,
  toLocationId: LocationId | null,
  avoidsChannels: boolean,
  noGoZones: string,
): number | null => {
  const { distances, queueDistancesForLocationPairs } = useDistances();
  const isFetching = useRef<boolean>(false);

  const distance =
    fromLocationId !== null && toLocationId !== null
      ? (distances.get(
          fromLocationId,
          toLocationId,
          avoidsChannels,
          null,
          noGoZones,
        )?.dist ?? null)
      : null;

  useEffect(() => {
    if (fromLocationId === null || toLocationId === null) {
      return;
    }

    if (distance === null) {
      if (isFetching.current) return;
      isFetching.current = true;
      queueDistancesForLocationPairs([
        [fromLocationId, toLocationId, avoidsChannels, null, noGoZones],
      ]);
    }

    if (isFetching.current && distance !== null) {
      isFetching.current = false;
    }
  }, [
    fromLocationId,
    toLocationId,
    avoidsChannels,
    queueDistancesForLocationPairs,
    distance,
    noGoZones,
  ]);

  return distance;
};
